import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { injectReducer } from 'store'
import reducer from './store'
import { useDispatch, useSelector } from 'react-redux'
import { getData, setTableData } from './store/dataSlice'
import {
    HiOutlineCheckCircle,
    HiOutlineEye,
    HiOutlineTrash,
    HiOutlineXCircle,
} from 'react-icons/hi'
import Tooltip from 'components/Ui/Tooltip'
import { ConfirmationModal, Modal, Table } from 'components/Ui'
import { deleteApiRecord } from 'services/InvoiceService'
import { Success } from 'utils/alerts'
import useAuth from 'utils/hooks/useAuth'
import { numberFormat } from 'components/helper'
import dayjs from 'dayjs'
import { ApproveRejectRecordApi } from 'services/RateCardService'
import { GrNotes } from 'react-icons/gr'
import { Field, Form, Formik } from 'formik'
import { Button, TextArea } from 'components/Form'
import * as Yup from 'yup'
injectReducer('pendingInvoices', reducer)

const validationSchema = Yup.object().shape({
    notes: Yup.string().required('Notes Required'),
})

export default function PendingInvoices() {
    const dispatch = useDispatch()
    const { checkPermission } = useAuth()
    const { data, tableData, loading } = useSelector(
        (state) => state.pendingInvoices.data,
    )
    const [invoice, setInvoice] = useState('')
    const [notesDialogOpen, setNotesDialogOpen] = useState(false)

    useEffect(() => {
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dispatch,
        tableData.sort,
        tableData.query,
        tableData.pageIndex,
        tableData.pageSize,
        tableData.selectedTab,
        tableData.service,
    ])

    const fetchData = () => dispatch(getData({ ...tableData }))

    const handleStatus = (id, data) => {
        ApproveRejectRecordApi(id, data)
            .then(() => {
                Success(
                    `Invoice moved to ${data.status} Invoices Successfully!`,
                )
                fetchData()
            })
            .catch((error) => error)
    }

    const handleDelete = (id) => {
        deleteApiRecord(id)
            .then(() => {
                Success('Deleted Successfully!')
                fetchData()
            })
            .catch((error) => error)
    }

    const handleSubmit = (values) => {
        setNotesDialogOpen(false)
        ApproveRejectRecordApi(invoice.id, values)
            .then(() => {
                Success('Submit successfully!')
                fetchData()
            })
            .catch((error) => error)
    }

    const columns = useMemo(
        () => [
            {
                field: 'invoice',
                header: 'Invoice',
                className: 'min-w-[150px]',
                renderCell: (row) =>
                    row?.invoice_id?.invoice.split('/')[
                        row?.invoice_id?.invoice.split('/').length - 1
                    ],
            },
            {
                field: 'invoice_num',
                header: 'Invoice Number',
                className: 'min-w-[150px]',
            },
            {
                field: 'invoiced_total',
                header: 'Invoice Total',
                className: 'min-w-[150px]',
                renderCell: (row) => numberFormat(row?.invoiced_total, 'AUD'),
            },
            {
                field: 'total_line_items',
                header: 'Total Line Items',
                className: 'min-w-[150px]',
            },
            {
                field: 'total_verified_line_items',
                header: 'Total Verified Line Items',
                className: 'min-w-[150px]',
            },
            {
                field: 'total_overcharged',
                header: 'Total Overcharged (Amount)',
                className: 'min-w-[150px]',
                renderCell: (row) =>
                    numberFormat(row?.total_overcharged, 'AUD'),
            },
            {
                field: 'total_undercharged',
                header: 'Total Undercharged (Amount)',
                className: 'min-w-[150px]',
                renderCell: (row) =>
                    numberFormat(row?.total_undercharged, 'AUD'),
            },
            {
                field: 'uploaded_at',
                header: 'Uploaded At',
                className: 'min-w-[150px]',
                renderCell: (row) =>
                    row?.invoice_id?.uploaded_at
                        ? dayjs(row?.invoice_id?.uploaded_at).format(
                              'DD-MMM-YYYY',
                          )
                        : '-',
            },
            {
                header: 'Action',
                field: 'action',
                className: 'px-4',
                renderCell: (row) => {
                    return (
                        <div className="flex items-center gap-3">
                            <Tooltip title={'Notes'} placement={'top'}>
                                <span
                                    className="text-xl cursor-pointer hover:text-red-500"
                                    onClick={() => {
                                        setInvoice(row)
                                        setNotesDialogOpen(true)
                                    }}
                                >
                                    <GrNotes />
                                </span>
                            </Tooltip>
                            <Tooltip title={'View'}>
                                <Link
                                    to={`/invoices/pending-invoices/${row.invoice_num}/result`}
                                    className="hover:text-primary"
                                >
                                    <HiOutlineEye size={20} />
                                </Link>
                            </Tooltip>
                            <Tooltip title={'Approve'} placement={'top'}>
                                <ConfirmationModal
                                    message={
                                        'Are you sure you want to move this invoice into approved invoices!'
                                    }
                                    onConfirm={() => {
                                        handleStatus(row.id, {
                                            status: 'Approved',
                                            invoice_num: row.invoice_num,
                                        })
                                    }}
                                >
                                    <span
                                        className={`cursor-pointer hover:text-primary`}
                                    >
                                        <HiOutlineCheckCircle size={20} />
                                    </span>
                                </ConfirmationModal>
                            </Tooltip>
                            <Tooltip title={'Reject'} placement={'top'}>
                                <ConfirmationModal
                                    message={
                                        'Are you sure you want to move this invoice into rejected invoices!'
                                    }
                                    onConfirm={() => {
                                        handleStatus(row.id, {
                                            status: 'Rejected',
                                            invoice_num: row.invoice_num,
                                        })
                                    }}
                                >
                                    <span
                                        className={`cursor-pointer hover:text-danger`}
                                    >
                                        <HiOutlineXCircle size={20} />
                                    </span>
                                </ConfirmationModal>
                            </Tooltip>
                            {checkPermission('extra-charges', 'delete') ? (
                                <Tooltip title={'Delete'}>
                                    <ConfirmationModal
                                        message={
                                            'Are you sure you want to delete this record!'
                                        }
                                        onConfirm={() => {
                                            handleDelete(row.id)
                                        }}
                                    >
                                        <span className="hover:text-danger">
                                            <HiOutlineTrash size={20} />
                                        </span>
                                    </ConfirmationModal>
                                </Tooltip>
                            ) : null}
                        </div>
                    )
                },
            },
        ],
        // eslint-disable-next-line
        [
            tableData.sort,
            tableData.query,
            tableData.pageIndex,
            tableData.pageSize,
            tableData.selectedTab,
            tableData.service,
        ],
    )

    return (
        <>
            <div className="flex flex-col gap-1 mb-5 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="font-semibold text-black text-title-md2 dark:text-white">
                    Pending Invoices
                </h2>
            </div>
            <Table
                loading={loading}
                filter={false}
                datas={data}
                columns={columns}
                // add_new={checkPermission('extra-charges', 'create') ? '/master-data/extra-charges/create' : ''}
                pagingData={tableData}
                onChange={(e) => dispatch(setTableData(e))}
            />
            <Modal
                show={notesDialogOpen}
                onClose={() => {
                    setNotesDialogOpen(false)
                }}
            >
                <div className="flex flex-col gap-1 m-3">
                    <h2 className="font-semibold text-black text-title-md">
                        {'Add Notes'}
                    </h2>
                </div>
                <div className="flex flex-col items-center">
                    <div className="w-full bg-white border rounded-sm overflow-auto max-h-150 border-stroke shadow-default dark:border-strokedark dark:bg-boxdark">
                        <div className="flex flex-col gap-3 p-6">
                            <Formik
                                initialValues={{
                                    notes: invoice.notes ? invoice.notes : '',
                                    invoice_num: invoice.invoice_num,
                                }}
                                validationSchema={validationSchema}
                                onSubmit={handleSubmit}
                            >
                                {({ values, touched, errors }) => (
                                    <Form>
                                        <div className="grid grid-cols-1 gap-x-4 gap-y-4">
                                            <Field name="notes">
                                                {({ field, form }) => (
                                                    <TextArea
                                                        label="Notes"
                                                        name="notes"
                                                        error={
                                                            errors.notes &&
                                                            touched.notes
                                                        }
                                                        message={errors.notes}
                                                        value={values?.notes}
                                                        onChange={(e) =>
                                                            form.setFieldValue(
                                                                field.name,
                                                                e.target.value,
                                                            )
                                                        }
                                                    />
                                                )}
                                            </Field>
                                        </div>
                                        <div className="flex items-center justify-end gap-4 mt-3">
                                            <Button
                                                className="h-10 w-18 text-[#F05540] border border-[#F05540] dark:text-[#F05540] dark:border-[#F05540]"
                                                onClick={() =>
                                                    setNotesDialogOpen(false)
                                                }
                                                type="button"
                                            >
                                                Discard
                                            </Button>
                                            <Button
                                                type="submit"
                                                className="flex items-center justify-center px-6 py-2 text-white transition border rounded-lg cursor-pointer hover:bg-opacity-90 border-[#F05540] bg-[#F05540] dark:border-[#F05540] dark:bg-[#F05540]"
                                            >
                                                Save
                                            </Button>
                                        </div>
                                    </Form>
                                )}
                            </Formik>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    )
}
